import { useEffect, useState } from 'react';
import { Button, Modal, Segmented } from '../../../components/ui';
import { useNoteCreate } from '../../../api/hooks';
import { useAuth } from '../../../../utils/Auth/AuthProvider';

// Artboard `.ta`: 12px / 14px padding, line-strong border, 2px slate focus outline.
const FIELD = 'min-h-[132px] w-full resize-y rounded-[12px] border border-line-strong bg-surface px-3.5 py-3 text-sm text-ink outline-none transition placeholder:text-muted focus:outline focus:outline-2 focus:outline-offset-1 focus:outline-slate';
const LABEL = 'mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted';
const MAX = 2000;

export type NoteVisibility = 'teachers_of_student' | 'counselor_admin_only';

/** Student 360 → "Add note": a short note staff leave about the student. Admins and
 *  counsellors choose who can see it; a teacher's note goes to the student's teachers. */
export default function AddNoteDialog({ isOpen, onClose, studentId, studentName, teachersLabel }: {
  isOpen: boolean; onClose: () => void; studentId: number;
  studentName?: string;
  /** "His teachers" / "Her teachers" / "Student's teachers". */
  teachersLabel: string;
}) {
  const { role } = useAuth();
  const create = useNoteCreate();
  const [body, setBody] = useState('');
  const [visibility, setVisibility] = useState<NoteVisibility>('teachers_of_student');
  const [error, setError] = useState<string | null>(null);
  const canChoose = role === 'school_admin' || role === 'counselor';

  useEffect(() => {
    if (isOpen) { setBody(''); setVisibility('teachers_of_student'); setError(null); }
  }, [isOpen]);

  async function save() {
    if (!body.trim()) return;
    setError(null);
    try {
      await create.mutateAsync({ student: studentId, body: body.trim(), visibility });
      onClose();
    } catch {
      setError('Could not save the note. Try again.');
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" eyebrow="Student note"
      title={`Add a note${studentName ? ` about ${studentName}` : ''}`}
      subtitle="Notes stay on the student's profile for the staff who can see them."
      initialFocus={() => document.getElementById('student-note-body')}
      footer={<>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} loading={create.isPending} disabled={!body.trim()}>Save note</Button>
      </>}>
      <div className="space-y-4">
        {error && <p role="alert" className="text-sm text-danger-ink">{error}</p>}
        <div>
          <label htmlFor="student-note-body" className={LABEL}>Note</label>
          <textarea id="student-note-body" value={body} maxLength={MAX} rows={5}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); save(); } }}
            placeholder={`What should other staff know about ${studentName || 'this student'}?`} className={FIELD} />
          <p className="mt-1 text-right font-mono text-[11px] text-muted">{body.length} / {MAX}</p>
        </div>
        <div>
          <span className={LABEL}>Who can see it</span>
          {canChoose ? (
            <Segmented<NoteVisibility> dense label="Note visibility" value={visibility} onChange={setVisibility}
              options={[{ key: 'teachers_of_student', label: teachersLabel }, { key: 'counselor_admin_only', label: 'Counsellors and admins' }]} />
          ) : (
            <p className="text-sm text-ink">{teachersLabel}, the counsellor and admins.</p>
          )}
        </div>
      </div>
    </Modal>
  );
}
